import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { tx } from '@/translations/translations';
import { BookOpen, Scale, CheckCircle2, Sparkles, ArrowRight } from 'lucide-react';

const MaterialSection = () => {
  const { language } = useLanguage();

  const t = {
    es: {
      intro: 'La Torá no nos dice explícitamente de qué material debe ser el tzitzit. Por eso la Guemará nos regala un debate entero — y dos tradiciones — que aún hoy moldean nuestra práctica.',
      debate: 'El Debate Talmúdico',
      debateSub: 'Guemará Menajot 39B',
      ishmaelName: 'Rabí Ishmael',
      ishmaelPos: 'Debe ser de lana',
      ishmaelExp: 'Cada vez que la Torá dice "begued" (prenda) se refiere específicamente a una prenda de lana.',
      ishmaelRef: 'Vaikra 13:47 — "begued tzemer"',
      rabaName: 'Raba',
      rabaPos: 'Cualquier material',
      rabaExp: 'La Torá usa el término "kanaf" (punta). Cualquier prenda con puntas en sus extremos está incluida en la mitzvá.',
      rabaRef: 'Bamidbar 15:38',
      posiciones: 'Las dos posturas posteriores',
      sefardi: 'Tradición Sefaradí',
      sefardiText: 'Maimónides, el Rif y el Shulján Aruj dictaminan como Rabí Ishmael: de Torá sólo con lana. Otros materiales cumplen un precepto rabínico.',
      ashkenazi: 'Tradición Ashkenazí',
      ashkenaziText: 'El Rosh, Rashí, Tosfot y el Ramá siguen a Raba: tzitzit de cualquier material cumple un precepto de la Torá.',
      summary: 'La conclusión práctica',
      summarySub: '¿Qué material cumple la mitzvá de la Torá?',
      materialWool: 'Lana',
      woolAll: 'Mitzvá de la Torá',
      woolSub: 'Según todas las opiniones',
      materialOther: 'Otros materiales',
      otherSome: 'Mitzvá según todos',
      otherSub: 'Según algunos, rabínica; según otros, de la Torá',
      finalTitle: 'Recomendación',
      final: 'Usar tzitzit de lana satisface ambas posturas y se considera mitzvá de la Torá para todas las tradiciones.',
    },
    en: {
      intro: 'The Torah does not explicitly tell us what material tzitzit must be made of. That\'s why the Gemara gives us an entire debate — and two traditions — that shape our practice to this day.',
      debate: 'The Talmudic Debate',
      debateSub: 'Gemara Menachot 39B',
      ishmaelName: 'Rabbi Ishmael',
      ishmaelPos: 'Must be wool',
      ishmaelExp: 'Whenever the Torah says "begued" (garment), it refers specifically to a wool garment.',
      ishmaelRef: 'Vaikra 13:47 — "begued tzemer"',
      rabaName: 'Raba',
      rabaPos: 'Any material',
      rabaExp: 'The Torah uses the term "kanaf" (corner). Any garment with corners is included in the mitzvah.',
      rabaRef: 'Bamidbar 15:38',
      posiciones: 'The two resulting positions',
      sefardi: 'Sephardic Tradition',
      sefardiText: 'Maimonides, the Rif and the Shuljan Aruj rule like Rabbi Ishmael: Torah-level only with wool. Other materials fulfill a rabbinic precept.',
      ashkenazi: 'Ashkenazi Tradition',
      ashkenaziText: 'The Rosh, Rashi, Tosafot and the Rama follow Raba: tzitzit of any material fulfills a Torah precept.',
      summary: 'The practical conclusion',
      summarySub: 'Which material fulfills the Torah mitzvah?',
      materialWool: 'Wool',
      woolAll: 'Torah Mitzvah',
      woolSub: 'According to all opinions',
      materialOther: 'Other materials',
      otherSome: 'Mitzvah per all',
      otherSub: 'Per some, rabbinic; per others, Torah-level',
      finalTitle: 'Recommendation',
      final: 'Wearing wool tzitzit satisfies both positions and is considered a Torah mitzvah across all traditions.',
    },
    he: {
      intro: 'התורה אינה אומרת לנו במפורש מאיזה חומר צריכה להיות הציצית. לכן הגמרא מעניקה לנו דיון שלם — ושתי מסורות — המעצבות את ההלכה עד היום.',
      debate: 'הדיון התלמודי',
      debateSub: 'גמרא מנחות לט ע״ב',
      ishmaelName: 'רבי ישמעאל',
      ishmaelPos: 'חייב להיות מצמר',
      ishmaelExp: 'בכל מקום שהתורה אומרת "בגד" הכוונה דווקא לבגד צמר.',
      ishmaelRef: 'ויקרא יג:מז — "בגד צמר"',
      rabaName: 'רבא',
      rabaPos: 'כל חומר',
      rabaExp: 'התורה משתמשת במילה "כנף". כל בגד עם כנפות כלול במצווה.',
      rabaRef: 'במדבר טו:לח',
      posiciones: 'שתי השיטות שנוצרו',
      sefardi: 'מסורת ספרדית',
      sefardiText: 'הרמב״ם, הרי״ף והשולחן ערוך פוסקים כרבי ישמעאל: מן התורה רק בצמר. חומרים אחרים מקיימים מצווה מדרבנן.',
      ashkenazi: 'מסורת אשכנזית',
      ashkenaziText: 'הרא״ש, רש״י, תוספות והרמ״א הולכים אחרי רבא: ציצית מכל חומר מקיימת מצווה מן התורה.',
      summary: 'המסקנה למעשה',
      summarySub: 'איזה חומר מקיים את מצוות התורה?',
      materialWool: 'צמר',
      woolAll: 'מצווה מן התורה',
      woolSub: 'לכל הדעות',
      materialOther: 'חומרים אחרים',
      otherSome: 'מצווה לכל הדעות',
      otherSub: 'יש אומרים מדרבנן; ויש אומרים מן התורה',
      finalTitle: 'המלצה',
      final: 'לבישת ציצית צמר יוצאת ידי שתי השיטות ונחשבת מצווה מן התורה לכל המסורות.',
    },
  };
  const L = t[language] || t.es;

  return (
    <div id="material-tzitzit" className="bg-slate-950 px-4 sm:px-6 pb-16 pt-6">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Intro */}
        <div className="bg-white/[0.04] border border-white/[0.08] rounded-2xl p-5 sm:p-7">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300/80 mb-1">
                {tx(language, 'Introducción', 'Introduction', 'הקדמה')}
              </p>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                {L.intro}
              </p>
            </div>
          </div>
        </div>

        {/* Debate — two rabbis side by side */}
        <div>
          <div className="text-center mb-5">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-cyan-400/80 mb-1">
              {L.debateSub}
            </p>
            <h3 className="text-2xl sm:text-3xl font-bold text-white">{L.debate}</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-3 sm:gap-4 items-stretch">
            {/* Rabbi Ishmael */}
            <div className="relative rounded-2xl border border-amber-400/20 bg-gradient-to-br from-amber-950/50 to-slate-900 p-5 sm:p-6 overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/5 rounded-full -translate-y-8 translate-x-8" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center font-serif text-lg font-black text-white shadow-lg shadow-amber-900/40">
                    א
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300/80">
                      {tx(language, 'Postura', 'Position', 'שיטה')}
                    </p>
                    <h4 className="text-base font-bold text-white">{L.ishmaelName}</h4>
                  </div>
                </div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-300 text-xs font-bold border border-amber-400/30 mb-3">
                  <CheckCircle2 className="w-3 h-3" />
                  {L.ishmaelPos}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">{L.ishmaelExp}</p>
                <p className="text-[11px] text-amber-300/60 italic mt-3 font-medium tracking-wide">
                  {L.ishmaelRef}
                </p>
              </div>
            </div>

            {/* Vs separator */}
            <div className="flex md:flex-col items-center justify-center">
              <div className="w-14 h-14 sm:w-12 sm:h-12 rounded-full bg-slate-800 border border-white/10 flex items-center justify-center font-black text-xs text-slate-400">
                VS
              </div>
            </div>

            {/* Raba */}
            <div className="relative rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-cyan-950/50 to-slate-900 p-5 sm:p-6 overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-cyan-500/5 rounded-full -translate-y-8 translate-x-8" />
              <div className="relative">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center font-serif text-lg font-black text-white shadow-lg shadow-cyan-900/40">
                    ב
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-cyan-300/80">
                      {tx(language, 'Postura', 'Position', 'שיטה')}
                    </p>
                    <h4 className="text-base font-bold text-white">{L.rabaName}</h4>
                  </div>
                </div>
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-cyan-500/15 text-cyan-300 text-xs font-bold border border-cyan-400/30 mb-3">
                  <CheckCircle2 className="w-3 h-3" />
                  {L.rabaPos}
                </div>
                <p className="text-sm text-slate-300 leading-relaxed">{L.rabaExp}</p>
                <p className="text-[11px] text-cyan-300/60 italic mt-3 font-medium tracking-wide">
                  {L.rabaRef}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Traditions: down-arrow flow */}
        <div>
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Scale className="w-4 h-4 text-white" />
            </div>
            <h3 className="text-lg font-bold text-white">{L.posiciones}</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Sefardi → Ishmael */}
            <div className="rounded-xl bg-white/[0.04] border border-white/[0.08] p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300/80">
                  {L.sefardi}
                </span>
                <ArrowRight className="w-3 h-3 text-slate-600" />
                <span className="text-[10px] font-bold text-slate-400">{L.ishmaelName}</span>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">{L.sefardiText}</p>
            </div>

            {/* Ashkenazi → Raba */}
            <div className="rounded-xl bg-white/[0.04] border border-white/[0.08] p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-cyan-300/80">
                  {L.ashkenazi}
                </span>
                <ArrowRight className="w-3 h-3 text-slate-600" />
                <span className="text-[10px] font-bold text-slate-400">{L.rabaName}</span>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">{L.ashkenaziText}</p>
            </div>
          </div>
        </div>

        {/* Summary — didactic comparison table */}
        <div>
          <div className="text-center mb-4">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-emerald-400/80 mb-1">
              {L.summary}
            </p>
            <h3 className="text-xl sm:text-2xl font-bold text-white">{L.summarySub}</h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Wool card */}
            <div className="relative rounded-2xl bg-gradient-to-br from-emerald-950/50 to-slate-900 border-2 border-emerald-400/30 p-5 overflow-hidden">
              <span className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 text-[9px] font-bold uppercase tracking-wider border border-emerald-400/40">
                <CheckCircle2 className="w-2.5 h-2.5" />
                {tx(language, 'Recomendado', 'Recommended', 'מומלץ')}
              </span>
              <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-400/80 mb-1">
                {tx(language, 'Material', 'Material', 'חומר')}
              </p>
              <h4 className="text-2xl font-black text-white mb-2">{L.materialWool}</h4>
              <div className="h-px bg-gradient-to-r from-emerald-400/50 to-transparent mb-3" />
              <p className="text-sm font-bold text-emerald-300 mb-0.5">{L.woolAll}</p>
              <p className="text-xs text-slate-400">{L.woolSub}</p>
            </div>

            {/* Other materials card */}
            <div className="relative rounded-2xl bg-white/[0.03] border border-white/[0.08] p-5">
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">
                {tx(language, 'Material', 'Material', 'חומר')}
              </p>
              <h4 className="text-2xl font-black text-white mb-2">{L.materialOther}</h4>
              <div className="h-px bg-gradient-to-r from-slate-500/40 to-transparent mb-3" />
              <p className="text-sm font-bold text-slate-300 mb-0.5">{L.otherSome}</p>
              <p className="text-xs text-slate-500">{L.otherSub}</p>
            </div>
          </div>

          {/* Final recommendation */}
          <div className="mt-4 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-500/5 border border-cyan-400/30 p-5 sm:p-6">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-cyan-900/40">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-cyan-300/80 mb-1">
                  {L.finalTitle}
                </p>
                <p className="text-sm sm:text-base text-white/95 leading-relaxed font-medium">
                  {L.final}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MaterialSection;
